import DeleteButton from '@/components/DeleteButton';
import EnvironmentTag from '@/components/EnvironmentTag';
import Page from '@/components/page/Page';
import DividerV2 from '@/components/v2/DividerV2';
import { useAnalytics } from '@/hooks/useAnalytics.js';
import setQuery from '@/lib/setQuery';
import { deleteWorkflowLog } from '@/redux/actions/gravityActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { actionModels } from '@shared/schema/actionModels.js';
import { getRunnerSizeDisplay, getRunnerSizeTooltip } from '@shared/schema/runnerSizes.js';
import { formatBytes } from '@shared/utils/formatBytes.js';
import { formatDuration } from '@shared/utils/formatDuration.js';
import { differenceInSeconds, format } from 'date-fns';
import { useMemo } from 'react';
import getDetailsLogTriggerMessage from './lib/getDetailsLogTriggerMessage';
import IconV2 from '@/components/v2/IconV2';
import { FiInfo } from '@/components/react-icons/main';

const getOperationStatsCopy = (stats) => {
  const copyArray = [];
  if (stats?.create > 0) {
    copyArray.push(`${stats?.create} created`);
  }
  if (stats?.update > 0) {
    copyArray.push(`${stats?.update} updated`);
  }
  if (stats?.delete > 0) {
    copyArray.push(`${stats?.delete} deleted`);
  }
  return copyArray.join(', ');
};

const DetailsInfoSection = () => {
  const activeLogId = useArgSelector(selectors.query, 'logId');
  const workflowId = useArgSelector(selectors.workflowId);
  const log = useArgSelector(selectors.workflowLogsById, activeLogId);
  const environment = useArgSelector(selectors.environmentById, log?.environmentId);
  const workflowSnapshotVersion = useArgSelector(workflowScreenSelectors.activeWorkflowSnapshotVersionById, log?.snapshotId) || 'Draft';
  const operationLogs = useArgSelector(selectors.workflowLogOperationLogsById, activeLogId) || [];
  const { trackEvent } = useAnalytics();
  const duration = differenceInSeconds(new Date(log?.finishedOn || log?.updatedOn), new Date(log?.startedOn));
  const environmentName = `${environment?.name} - ${workflowSnapshotVersion}`;
  const triggerStep = log?.meta?.triggerStep;
  const triggerStepMessage = getDetailsLogTriggerMessage(triggerStep);
  const noOperationsConsumed = log?.operations && Number(log?.operations) === 0;
  const endUserLabel = [log?.endUserEmail, log?.endUserName].filter(Boolean).join(' - ') || log?.endUserId;

  const operationStatsByModel = useMemo(() => {
    const stats = {};
    operationLogs.forEach((log) => {
      if (!log.model) {
        return;
      }
      if (!stats[log.model]) {
        stats[log.model] = {
          create: 0,
          update: 0,
          delete: 0
        };
      }
      if (log.actionStatus === 'success') {
        stats[log.model][log.operation]++;
      }
    });
    return stats;
  }, [operationLogs]);

  const errorCount = useMemo(() => operationLogs.filter((log) => log.actionStatus === 'error').length, [operationLogs]);
  const warningCount = useMemo(() => operationLogs.filter((log) => log.actionStatus === 'warn').length, [operationLogs]);

  const noCreatedData = useMemo(() => Object.values(operationStatsByModel).every((stats) => stats.create === 0), [operationStatsByModel]);
  const noUpdatedData = useMemo(() => Object.values(operationStatsByModel).every((stats) => stats.update === 0), [operationStatsByModel]);
  const noDeletedData = useMemo(() => Object.values(operationStatsByModel).every((stats) => stats.delete === 0), [operationStatsByModel]);

  const noChangedData = useMemo(() => noCreatedData && noUpdatedData && noDeletedData, [noCreatedData, noUpdatedData, noDeletedData]);

  const noData = useMemo(() => {
    return noChangedData && errorCount === 0 && warningCount === 0;
  }, [noChangedData, errorCount, warningCount]);

  // const workflowFailedOrInterrupted = log?.statusId === 3 || log?.statusId === 5;

  const handleDeleteClick = () => {
    trackEvent('workflow-deleteLog');
    deleteWorkflowLog({ workflowId, workflowLogId: activeLogId });
    setQuery({ time: null, logTab: null, logId: null });
  };

  const isLoadingLogById = useArgSelector(selectors.isLoading, 'getWorkflowLogById');

  if (isLoadingLogById) {
    return (
      <Page.Section variant="chromeless" className="infos-section">
        <div className="infos-container">
          <div className="log-info">
            <div className="info-label">Log Details</div>
            <div className="info-value">Loading...</div>
          </div>

          <DividerV2 mode="vertical" />

          <div className="log-info">
            <div className="info-value">No changes</div>
          </div>
        </div>

        <DeleteButton disabled={true} container={document.querySelector('.workflow-logs-modal')} align="start" />
      </Page.Section>
    );
  }

  return (
    <Page.Section variant="chromeless" className="infos-section">
      <div className="infos-container">
        {log?.meta?.runnerSize && (
          <div className="log-info">
            <div className="info-label">Runner Size</div>
            <div className="info-value" data-title={getRunnerSizeTooltip(log?.meta?.runnerSize)}>
              {getRunnerSizeDisplay(log?.meta?.runnerSize)}
            </div>
          </div>
        )}
        <div className="log-info">
          <div className="info-label">Started On</div>
          <div className="info-value">{log?.startedOn ? format(new Date(log.startedOn), 'MMM d, yyyy h:mm:ss a') : null}</div>
        </div>
        {triggerStepMessage && (
          <div className="log-info">
            <div className="info-label">Started By</div>
            <div className="info-value">{triggerStepMessage}</div>
          </div>
        )}
        {log?.installationId && (
          <div className="log-info">
            <div className="info-label">Installation</div>
            <div className="info-value">{log.installationId}</div>
          </div>
        )}
        {endUserLabel && (
          <div className="log-info">
            <div className="info-label">End User</div>
            <div className="info-value" data-title={log?.endUserId}>
              {endUserLabel}
            </div>
          </div>
        )}
        <div className="log-info">
          <div className="info-label">Version</div>
          <div className="info-value">{log?.environmentId ? <EnvironmentTag environmentId={log?.environmentId} overrideName={environmentName} /> : workflowSnapshotVersion}</div>
        </div>
        <div className="log-info">
          <div className="info-label">Duration</div>
          <div className="info-value">{formatDuration(duration, { details: true })}</div>
        </div>
        <div className="log-info tasks-used">
          <div className="info-label">Tasks Used{!triggerStep && noOperationsConsumed && <IconV2 icon={FiInfo} size={10} className={'light-medium'} title={"Manual runs don't count towards your tasks limit."} />}</div>
          <div className="info-value">{log?.operations}</div>
        </div>

        {log?.meta?.metrics && (
          <>
            <div className="log-info">
              <div className="info-label">CPU Time</div>
              <div className="info-value">{log?.meta?.metrics?.cpuSeconds?.toFixed(2)}s</div>
            </div>
            <div className="log-info">
              <div className="info-label">Peak Memory</div>
              <div className="info-value">{formatBytes(log?.meta?.metrics?.rssMax)}</div>
            </div>
            <div className="log-info">
              <div className="info-label">Avg Memory</div>
              <div className="info-value">{formatBytes(log?.meta?.metrics?.rssAverage)}</div>
            </div>
            {log?.meta?.metrics?.heapUsed && (
              <>
                <div className="log-info">
                  <div className="info-label">Heap Used</div>
                  <div className="info-value">{formatBytes(log?.meta?.metrics?.heapUsed)}</div>
                </div>
                <div className="log-info">
                  <div className="info-label">Heap Total</div>
                  <div className="info-value">{formatBytes(log?.meta?.metrics?.heapTotal)}</div>
                </div>
                <div className="log-info">
                  <div className="info-label">Heap External</div>
                  <div className="info-value">{formatBytes(log?.meta?.metrics?.external)}</div>
                </div>
              </>
            )}
          </>
        )}

        <DividerV2 mode="vertical" />

        {noData ? (
          <div className="log-info">
            <div className="info-value">No changes</div>
          </div>
        ) : (
          <>
            {!noChangedData &&
              Object.entries(operationStatsByModel).map(([model, stats]) => (
                <div key={model} className="log-info">
                  <div className="info-label">{actionModels[model]?.nicknamePlural || model}</div>
                  <div className="info-value">{getOperationStatsCopy(stats)}</div>
                </div>
              ))}
            {errorCount > 0 && (
              <div className="log-info">
                <div className="info-label">Errors</div>
                <div className="info-value">{errorCount}</div>
              </div>
            )}
            {warningCount > 0 && (
              <div className="log-info">
                <div className="info-label">Warnings</div>
                <div className="info-value">{warningCount}</div>
              </div>
            )}
          </>
        )}
      </div>

      <DeleteButton onClick={handleDeleteClick} container={document.querySelector('.workflow-logs-modal')} align="start" />
    </Page.Section>
  );
};

export default DetailsInfoSection;
